import React, {type ClipboardEvent, useCallback, useContext, useMemo, useState} from 'react';

import debounce from 'lodash.debounce';
import isString from 'lodash.isstring';
import {UNCLEAR_QUERY_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {runUserSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField, UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Icon, {Icons} from '@pb/kit-component-icon';
import {PillsAutosuggest} from '@pb/kit-component-pills-autosuggest';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {UsersSuggestList} from './users-suggest-list';
import {splitQuery} from './utils';

import type {PillProps} from '@pb/kit-component-strip-of-pills';
import type {UserSearchResponse} from 'src/openapi/admin/models/UserSearchResponse';
import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';
import type {ServerResponse} from 'src/typedef';

type Props = {
    id: string,
    accountId: number,
    selectedUsers: (UserSearchResult | string)[],
    setSelectedUsers: (users: (UserSearchResult | string)[]) => void,
    placeholder: string,
    onFocus?: () => void,
};

export const UsersSuggest = ({id, accountId, selectedUsers, setSelectedUsers, placeholder, onFocus}: Props) => {
    const apiDispatch = useAppDispatch();
    const {abort, createAbortSignal} = useAbortSignal();
    const {setErrorMessage} = useContext(MessageContext);

    const [query, setQuery] = useState('');
    const [response, setResponse] = useState<ServerResponse<UserSearchResponse> | null>(null);

    const findUsersByEmails = useCallback(async (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text');
        const emails = splitQuery(text);

        const emailsForCheck = emails.filter(value => value.length >= 5);
        const usersResponse = await apiDispatch(runUserSearch(
            {
                userSearchRequest: {
                    searchCriteria: {
                        userEmails: emailsForCheck.map(email => email.trim()),
                        accountIds: [accountId],
                    },
                    userSortCriteria: {
                        columnId: UserSortFields.EMAIL,
                        direction: SortDirections.ASC,
                    },
                    pagination: {
                        currentPage: 1,
                        pageSize: 250,
                    },
                },
            },
        ));

        if (usersResponse.payload.success) {
            const {result} = usersResponse.payload.data;
            const users = emails.map(value => {
                const user = result.find(({email}) => email === value);

                return user || value;
            });

            if (users.length) {
                setSelectedUsers([
                    ...selectedUsers.filter(value => {
                        if (isString(value)) {
                            return !users.includes(value);
                        } else {
                            return !users.find(newValue => {
                                if (isString(newValue)) {
                                    return newValue === value.email;
                                } else {
                                    return newValue.email === value.email;
                                }
                            });
                        }
                    }),
                    ...users,
                ]);
            }
        } else {
            setErrorMessage(getError({error: usersResponse.payload.data, separator: ''}));
        }
    }, [accountId, setErrorMessage, apiDispatch, selectedUsers, setSelectedUsers]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce(async (value: string) => {
        const signal = createAbortSignal();
        const email = value.trim();

        if (!email) {
            abort();
            setResponse(null);
            return;
        }

        if (email.length < 3) {
            setResponse({error: {message: UNCLEAR_QUERY_MESSAGE}});
            abort();
            return;
        }

        const usersResponse = await apiDispatch(runUserSearch({
            userSearchRequest: {
                searchCriteria: {
                    accountIds: [accountId],
                    userEmails: [email],
                },
                userSortCriteria: {
                    columnId: UserSortField.EMAIL,
                    direction: SortDirections.ASC,
                },
                pagination: {
                    currentPage: 1,
                    pageSize: 25,
                },
            },
        }, {signal}));

        if (usersResponse.payload.success) {
            setResponse({data: usersResponse.payload.data});
        } else {
            setResponse({error: {message: 'Can\'t suggest a user. Service is temporary unavailable'}});
        }
    }, 1000), [createAbortSignal, accountId, apiDispatch, abort]);

    const changeQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setResponse(null);
        setQuery(value);
        getSuggestions(value);
    }, [getSuggestions]);

    const selectItem = useCallback((value: UserSearchResult) => {
        setSelectedUsers([
            ...selectedUsers.filter(selected => {
                if (isString(selected)) {
                    return selected !== value.email;
                } else {
                    return selected.email !== value.email;
                }
            }),
            value,
        ]);
        setQuery('');
        setResponse(null);
    }, [selectedUsers, setSelectedUsers]);

    const clearAllUsers = useCallback(() => {
        setSelectedUsers([]);
        setQuery('');
        setResponse(null);
    }, [setSelectedUsers]);

    const onBlur = useCallback(() => {
        if (query) {
            setSelectedUsers([
                ...selectedUsers.filter(selected => {
                    if (isString(selected)) {
                        return selected !== query;
                    } else {
                        return selected.email !== query;
                    }
                }),
                query,
            ]);
            setQuery('');
        }
    }, [query, selectedUsers, setSelectedUsers]);

    const removeUser = useCallback((index: number) => {
        setSelectedUsers(selectedUsers.filter((_, i) => i !== index));
    }, [selectedUsers, setSelectedUsers]);

    const pills: PillProps[] = useMemo(() => selectedUsers.map((value, i) => ({
        variant: isString(value) ? 'EXCLUSION' : 'DEFAULT',
        name: isString(value) ? value : value.email,
        onRemove: () => removeUser(i),
        removable: true,
    })), [selectedUsers, removeUser]);

    return (
        <PillsAutosuggest
            onPaste={findUsersByEmails}
            id={id}
            value={query}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={changeQuery}
            placeholder={selectedUsers.length ? 'Search for users by email' : placeholder}
            onItemSelect={selectItem}
            startAdornment={<Icon type={Icons.SEARCH} className="pointer-events-none"/>}
            pills={pills}
            onClearAll={clearAllUsers}>
            <UsersSuggestList
                id={`${id}-list`}
                selectedUsers={selectedUsers}
                response={response}
            />
        </PillsAutosuggest>
    );
};
